import React, { useMemo, useState } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip as RechartsTooltip, LineChart, Line, XAxis, YAxis, CartesianGrid, BarChart, Bar } from 'recharts';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../context/ThemeContext';
import { CardBox, Badge, Table } from './UI';
import { IconChartBar, IconTrendingUp } from '@tabler/icons-react';

const GlobalDashboard = ({ overviewData, viewMode = 'simple' }) => {
  const { t, i18n } = useTranslation();
  const { isDark } = useTheme();
  const [disabledCategories, setDisabledCategories] = useState(new Set());

  // Recharts doesn't read Tailwind dark: utilities, so we hand it a palette.
  const chartPalette = isDark
    ? {
        gridStroke: '#374151',
        axisTick: '#9ca3af',
        axisLine: '#374151',
        tooltipBg: '#1f2937',
        tooltipBorder: '#374151',
        tooltipText: '#f3f4f6',
        line: '#34d399',
        lineDotStroke: '#1f2937',
        bar: '#34d399',
      }
    : {
        gridStroke: '#e5e7eb',
        axisTick: '#6b7280',
        axisLine: '#e5e7eb',
        tooltipBg: '#ffffff',
        tooltipBorder: '#e5e7eb',
        tooltipText: '#111827',
        line: '#9ec77f',
        lineDotStroke: '#ffffff',
        bar: '#9ec77f',
      };

  // Default data structure
  const defaultData = useMemo(() => ({
    hasData: false,
    totalBatches: 0,
    totalExecutions: 0,
    dateRange: { earliest: null, latest: null },
    metrics: {
      averageClientScore: 0,
      clientVisibilityRate: 0,
      totalCompetitors: 0,
      successRate: 0,
      totalSources: 0,
      uniqueDomains: 0
    },
    engines: {},
    topCompetitors: [],
    trackedCompetitors: [],
    topSources: [],
    bestQueries: [],
    sourceCategories: {},
    queryCategoryRankings: [],
    visibilityTimeSeries: []
  }), []);

  const data = overviewData || defaultData;

  const getEngineIcon = (engine) => {
    const engineName = engine.toLowerCase();
    const logoMap = {
      'openai': '/logos/chatgpt.png',
      'chatgpt': '/logos/chatgpt.png',
      'gemini': '/logos/gemini.png',
      'perplexity': '/logos/perplexity.png'
    };

    const logoSrc = logoMap[engineName];
    if (logoSrc) {
      return (
        <img
          src={logoSrc}
          alt={`${engine} logo`}
          className="w-8 h-8 rounded-full object-cover"
        />
      );
    }
    return <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700"></div>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const locale = i18n.language === 'he' ? 'he-IL' : 'en-US';
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Category translations mapping
  const getCategoryTranslation = (category) => {
    const translationKey = `dashboard.categories.${category}`;
    const translated = t(translationKey);
    // If translation doesn't exist, return original
    return translated === translationKey ? category : translated;
  };

  // Group sources by category for pie chart
  const sourceChartData = useMemo(() => {
    if (!data.sourceCategories || Object.keys(data.sourceCategories).length === 0) {
      return [];
    }

    return Object.entries(data.sourceCategories)
      .filter(([name]) => name !== 'Unknown') // Filter out Unknown category
      .map(([name, stats]) => ({
        name,
        displayName: getCategoryTranslation(name),
        value: stats.count,
        domains: stats.domains
      }))
      .sort((a, b) => b.value - a.value);
  }, [data.sourceCategories, t]);

  // Filter chart data based on disabled categories
  const filteredChartData = useMemo(() => {
    return sourceChartData.filter(item => !disabledCategories.has(item.name));
  }, [sourceChartData, disabledCategories]);

  // Handle category click to toggle enable/disable
  const handleCategoryClick = (data) => {
    // Handle both direct data (from pie click) and legend click
    let categoryName = data?.name || data;

    // Find the original category name from displayName if needed
    const category = sourceChartData.find(item =>
      item.displayName === categoryName || item.name === categoryName
    );

    if (category) {
      setDisabledCategories(prev => {
        const newSet = new Set(prev);
        if (newSet.has(category.name)) {
          newSet.delete(category.name);
        } else {
          newSet.add(category.name);
        }
        return newSet;
      });
    }
  };

  // Prepare engine source distribution data for bar chart
  const engineSourceData = useMemo(() => {
    if (!data.topSources || data.topSources.length === 0) {
      return [];
    }

    const engineCounts = {};
    data.topSources.forEach(source => {
      if (source.engines && Array.isArray(source.engines)) {
        source.engines.forEach(engine => {
          engineCounts[engine] = (engineCounts[engine] || 0) + source.count;
        });
      }
    });

    return Object.entries(engineCounts)
      .map(([engine, count]) => ({
        engine: engine === 'openai' ? 'ChatGPT' : engine.charAt(0).toUpperCase() + engine.slice(1),
        count
      }))
      .sort((a, b) => b.count - a.count);
  }, [data.topSources]);

  // Colors for pie chart — lime/olive/warm-gray brand palette (Pass 7).
  // Top 6 categories get the on-brand tones; overflow keeps saturated accents.
  const COLORS = {
    'Competitor': '#9ec77f',     // Olive — primary, most common
    'Ranking': '#d8f9b8',        // Brand lime
    'Comparison': '#5fa05c',     // Deeper green
    'UserReviews': '#374151',    // Warm dark gray
    'BlogPost': '#9ca3af',       // Warm mid gray
    'News': '#6b7280',           // Warm darker gray
    'Announcements': '#3b82f6',  // Blue (overflow)
    'Podcasts': '#10b981',       // Green (overflow)
    'SocialMedia': '#f59e0b',    // Amber (overflow)
    'Wikipedia': '#ef4444',      // Red (overflow)
    'Reddit': '#8b5cf6',         // Purple (overflow)
    'Unknown': '#cbd5e1'         // Light gray
  };

  if (!data.hasData) {
    return (
      <CardBox className="p-12">
        <div className="text-center py-16">
          <IconChartBar size={64} className="mx-auto text-gray-400 dark:text-gray-500 mb-6" />
          <h3 className="text-xl font-semibold mb-2 text-gray-900 dark:text-gray-100">{t('dashboard.overview.empty.title')}</h3>
          <p className="text-base text-gray-600 dark:text-gray-400">
            {t('dashboard.overview.empty.message')}
          </p>
        </div>
      </CardBox>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <CardBox>
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {t('dashboard.overview.globalAnalyticsDashboard') || 'Global Analytics Dashboard'}
          </h2>
          {data.dateRange.earliest && (
            <Badge color="info">
              {formatDate(data.dateRange.earliest)} - {formatDate(data.dateRange.latest)}
            </Badge>
          )}
        </div>
      </CardBox>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 metric-grid-enter">
        <CardBox className="metric-card-accent">
          <div className="text-gray-600 dark:text-gray-400 text-sm font-medium mb-2">
            {t('dashboard.overview.metrics.visibility') || 'Visibility'}
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            {data.metrics.clientVisibilityRate}%
          </div>
        </CardBox>

        <CardBox className="metric-card-accent">
          <div className="text-gray-600 dark:text-gray-400 text-sm font-medium mb-2">
            {t('dashboard.overview.metrics.competitors') || 'Competitors'}
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            {data.metrics.totalCompetitors}
          </div>
        </CardBox>

        <CardBox className="metric-card-accent">
          <div className="text-gray-600 dark:text-gray-400 text-sm font-medium mb-2">
            {t('dashboard.overview.metrics.sources') || 'Sources'}
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            {data.metrics.uniqueDomains}
          </div>
        </CardBox>
      </div>

      {/* Engine Performance */}
      {Object.keys(data.engines).length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-6">
            {t('dashboard.overview.metrics.enginePerformance') || 'Engine Performance'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {Object.entries(data.engines).map(([engine, stats]) => (
              <CardBox key={engine}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    {getEngineIcon(engine)}
                    <span className="text-base font-semibold text-gray-900 dark:text-gray-100">
                      {engine === 'openai' ? 'ChatGPT' : engine.charAt(0).toUpperCase() + engine.slice(1)}
                    </span>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Visibility</span>
                    <span className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                      {stats.visibilityRate}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${
                        stats.visibilityRate >= 50 ? 'bg-green-500' :
                        stats.visibilityRate >= 25 ? 'bg-yellow-500' :
                        'bg-red-500'
                      }`}
                      style={{ width: `${stats.visibilityRate}%` }}
                    />
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {stats.clientMentions} of {stats.count} mentions
                  </div>
                </div>
              </CardBox>
            ))}
          </div>
        </div>
      )}

      {/* Visibility Over Time Chart */}
      {data.visibilityTimeSeries && data.visibilityTimeSeries.length > 0 && (
        <CardBox
          title={t('dashboard.overview.metrics.visibilityOverTime') || 'Visibility Over Time'}
        >
          <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.visibilityTimeSeries} margin={{ top: 10, right: 30, left: 20, bottom: 20 }}>
                  <defs>
                    <linearGradient id="colorVisibility" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={chartPalette.line} stopOpacity={0.2}/>
                      <stop offset="95%" stopColor={chartPalette.line} stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={chartPalette.gridStroke} vertical={false} />
                  <XAxis
                    dataKey="week"
                    tick={{ fontSize: 12, fill: chartPalette.axisTick, fontWeight: 500 }}
                    axisLine={{ stroke: chartPalette.axisLine }}
                    tickLine={{ stroke: chartPalette.axisLine }}
                  />
                  <YAxis
                    domain={[0, 100]}
                    tick={{ fontSize: 12, fill: chartPalette.axisTick, fontWeight: 500 }}
                    axisLine={{ stroke: chartPalette.axisLine }}
                    tickLine={{ stroke: chartPalette.axisLine }}
                    label={{
                      value: 'Visibility %',
                      angle: -90,
                      position: 'insideLeft',
                      style: {
                        textAnchor: 'middle',
                        fill: chartPalette.axisTick,
                        fontSize: '12px',
                        fontWeight: 500
                      }
                    }}
                  />
                  <RechartsTooltip
                    contentStyle={{
                      backgroundColor: chartPalette.tooltipBg,
                      border: `1px solid ${chartPalette.tooltipBorder}`,
                      borderRadius: '12px',
                      padding: '12px',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    }}
                    labelStyle={{
                      color: chartPalette.tooltipText,
                      fontWeight: '600',
                      marginBottom: '4px'
                    }}
                    itemStyle={{ color: chartPalette.tooltipText }}
                    formatter={(value, name) => [`${value}%`, 'Visibility']}
                  />
                  <Line
                    type="monotone"
                    dataKey="visibility"
                    stroke={chartPalette.line}
                    strokeWidth={3}
                    dot={{ fill: chartPalette.line, r: 5, strokeWidth: 2, stroke: chartPalette.lineDotStroke }}
                    activeDot={{ r: 7, strokeWidth: 2 }}
                    fill="url(#colorVisibility)"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
        </CardBox>
      )}

      {/* Charts Grid */}
      {(sourceChartData.length > 0 || engineSourceData.length > 0) && (
        <div className="grid md:grid-cols-2 gap-8">
          {/* Category Distribution Pie Chart */}
          {sourceChartData.length > 0 && (
            <CardBox
              title={t('dashboard.overview.metrics.categoryDistribution')}
              subtitle={t('dashboard.overview.metrics.clickToToggle')}
            >
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={filteredChartData.length > 0 ? filteredChartData : sourceChartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ percent }) => percent > 0.05 ? `${(percent * 100).toFixed(0)}%` : ''}
                      outerRadius={110}
                      fill="#8884d8"
                      dataKey="value"
                      nameKey="displayName"
                      onClick={handleCategoryClick}
                      style={{ cursor: 'pointer' }}
                      paddingAngle={2}
                    >
                      {(filteredChartData.length > 0 ? filteredChartData : sourceChartData).map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={disabledCategories.has(entry.name) ? '#e5e7eb' : (COLORS[entry.name] || COLORS.Unknown)}
                          style={{ opacity: disabledCategories.has(entry.name) ? 0.3 : 1 }}
                        />
                      ))}
                    </Pie>
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: chartPalette.tooltipBg,
                        border: `1px solid ${chartPalette.tooltipBorder}`,
                        borderRadius: '12px',
                        padding: '12px',
                      }}
                      labelStyle={{ color: chartPalette.tooltipText }}
                      itemStyle={{ color: chartPalette.tooltipText }}
                    />
                    <Legend
                      verticalAlign="bottom"
                      height={36}
                      onClick={handleCategoryClick}
                      wrapperStyle={{ cursor: 'pointer', color: chartPalette.axisTick }}
                      formatter={(value) => {
                        // Find the original name for this displayName
                        const category = sourceChartData.find(item => item.displayName === value);
                        const isDisabled = category && disabledCategories.has(category.name);

                        return (
                          <span style={{
                            textDecoration: isDisabled ? 'line-through' : 'none',
                            opacity: isDisabled ? 0.5 : 1
                          }}>
                            {value}
                          </span>
                        );
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardBox>
          )}

          {/* Sources by Engine Bar Chart */}
          {engineSourceData.length > 0 && (
            <CardBox
              title={t('dashboard.overview.metrics.sourcesByEngine') || 'Sources by Engine'}
            >
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={engineSourceData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                    <defs>
                      <linearGradient id="colorBar" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={chartPalette.bar} stopOpacity={0.9}/>
                        <stop offset="95%" stopColor={chartPalette.bar} stopOpacity={0.7}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={chartPalette.gridStroke} vertical={false} />
                    <XAxis
                      dataKey="engine"
                      tick={{ fontSize: 12, fill: chartPalette.axisTick, fontWeight: 500 }}
                      axisLine={{ stroke: chartPalette.axisLine }}
                      tickLine={{ stroke: chartPalette.axisLine }}
                    />
                    <YAxis
                      tick={{ fontSize: 12, fill: chartPalette.axisTick, fontWeight: 500 }}
                      axisLine={{ stroke: chartPalette.axisLine }}
                      tickLine={{ stroke: chartPalette.axisLine }}
                    />
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: chartPalette.tooltipBg,
                        border: `1px solid ${chartPalette.tooltipBorder}`,
                        borderRadius: '12px',
                        padding: '12px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                      }}
                      labelStyle={{ color: chartPalette.tooltipText }}
                      itemStyle={{ color: chartPalette.tooltipText }}
                    />
                    <Bar dataKey="count" fill="url(#colorBar)" radius={[12, 12, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardBox>
          )}
        </div>
      )}

      {/* Tracked Competitors */}
      {data.trackedCompetitors && data.trackedCompetitors.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-6 flex items-center gap-2">
            {t('dashboard.overview.metrics.trackedCompetitors') || 'Tracked Competitors'}
            <Badge color="info" size="sm">
              {t('dashboard.overview.metrics.monitored') || 'Monitored'}
            </Badge>
          </h3>
          <div className="space-y-3">
            {data.trackedCompetitors.map((competitor, idx) => (
              <CardBox key={competitor.id || competitor.name || idx} hover>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <span className="text-2xl font-bold text-gray-400 dark:text-gray-500">
                      {idx + 1}
                    </span>
                    <div>
                      <div className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">
                        {competitor.name}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {competitor.queries || 0} queries • {competitor.mentions || 0} mentions
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                      {competitor.visibilityRate || 0}%
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">visibility</div>
                  </div>
                </div>
              </CardBox>
            ))}
          </div>
        </div>
      )}

      {/* Top Sources */}
      {data.topSources && data.topSources.length > 0 && viewMode === 'detailed' && (
        <Table
          title={t('dashboard.overview.metrics.topSources') || 'Top Sources'}
          columns={[
            { key: 'domain', label: 'Domain' },
            { key: 'category', label: 'Category', render: (value) => value || 'Unknown' },
            { key: 'count', label: 'Mentions' },
            {
              key: 'engines',
              label: 'Engines',
              render: (engines) => (
                <div className="flex gap-2">
                  {engines?.map((engine, i) => (
                    <Badge key={i} color="info" size="sm">
                      {engine === 'openai' ? 'ChatGPT' : engine.charAt(0).toUpperCase() + engine.slice(1)}
                    </Badge>
                  ))}
                </div>
              )
            }
          ]}
          data={data.topSources.slice(0, 10)}
          hoverable
          striped
        />
      )}
    </div>
  );
};

export default GlobalDashboard;